import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Github, Linkedin, Twitter, Code, Palette, Lightbulb, Rocket, Globe, HelpCircle } from "lucide-react";
import { DiscordIcon } from "@/components/icons/DiscordIcon";
import { ThreadsIcon } from "@/components/icons/ThreadsIcon";
import { SEO } from "@/components/seo/SEO";

const About = () => {
  const navigate = useNavigate();
  const technicalSkills = [
    "React & TypeScript",
    "Vite & Next.js",
    "PostgreSQL",
    "AI/LLM Integration",
    "Full-stack Development",
    "API Design",
    "Real-time Applications",
  ];

  const designSkills = [
    "UI/UX Design",
    "Responsive & Mobile-first",
    "Accessibility (a11y)",
    "Design Systems",
    "User Research",
    "Product Strategy",
  ];

  const socialLinks = [
    { icon: Github, href: "https://github.com/lyderdev", label: "GitHub" },
    { icon: Linkedin, href: "https://www.linkedin.com/in/l%E1%BB%A3i-v%C4%83n-645a09222/", label: "LinkedIn" },
    { icon: Twitter, href: "https://x.com/lyder_dev", label: "X (Twitter)" },
    { icon: ThreadsIcon, href: "https://www.threads.com/@loicoder", label: "Threads" },
    { icon: DiscordIcon, href: "https://discord.gg/ACj3Ncag", label: "Discord" },
  ];

  return (
    <>
      <SEO
        title="About Lyder - Developer & Creator"
        description="Learn about the developer behind Lyder Talk, an AI-powered speaking practice platform. Discover skills, expertise, and current focus in web development and language learning technology."
        url="/about"
        keywords="Lyder Talk developer, web developer, language learning technology, AI speaking practice, full-stack developer"
      />
      <div className="bg-gradient-to-br from-background via-background to-muted/30">
        <div className="container mx-auto px-4 py-12 max-w-5xl">

          {/* Profile Header */}
          <Card className="mb-8 border-2 shadow-lg">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <Avatar className="w-32 h-32 border-4 border-primary">
                  <AvatarImage src="/ghibli-illustration.png" alt="Lyder" />
                  <AvatarFallback className="text-4xl font-bold bg-gradient-to-br from-primary to-secondary">
                    L
                  </AvatarFallback>
                </Avatar>
              </div>
              <CardTitle as="h1" className="text-4xl mb-2 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                About Lyder
              </CardTitle>
              <CardDescription className="text-lg">
                Welcome to my personal space
              </CardDescription>
            </CardHeader>
          </Card>

          <div className="grid gap-8 md:grid-cols-2">
            {/* Who I Am */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Code className="w-5 h-5 text-primary" />
                  </div>
                  <CardTitle>Who I Am</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-muted-foreground">
                  Developer & creator of Lyder Talk
                </p>
                <p className="text-sm text-muted-foreground">
                  I'm passionate about building technology that helps people learn and grow.
                  As a language learner myself, I understand the challenges of practicing
                  speaking skills, which inspired me to create Lyder Talk—an AI-powered
                  platform designed to make language practice accessible and effective.
                </p>
              </CardContent>
            </Card>

            {/* My Work */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Globe className="w-5 h-5 text-primary" />
                  </div>
                  <CardTitle>My Work</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-muted-foreground">
                  Building Lyder Talk—an AI-powered speaking practice platform
                </p>
                <p className="text-sm text-muted-foreground">
                  Lyder Talk combines AI technology with language learning best practices
                  to help users practice speaking in 16 topic categories. Features include
                  AI-generated topics, vocabulary building with mind maps, study groups,
                  and community sharing—all designed to make speaking practice engaging and effective.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Skills & Expertise */}
          <Card className="mt-8 hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Lightbulb className="w-5 h-5 text-primary" />
                </div>
                Skills & Expertise
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Technical Skills */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Code className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold">Technical Skills</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {technicalSkills.map((skill) => (
                    <Badge key={skill} variant="secondary" className="text-sm py-1 px-3">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Design & Creative */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Palette className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold">Design & Creative</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {designSkills.map((skill) => (
                    <Badge key={skill} variant="outline" className="text-sm py-1 px-3">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Current Focus */}
          <Card className="mt-8 hover:shadow-lg transition-shadow bg-gradient-to-br from-primary/5 to-secondary/5 border-2">
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Rocket className="w-5 h-5 text-primary" />
                </div>
                <CardTitle>Current Focus</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                I'm actively developing and improving Lyder Talk, focusing on expanding
                AI capabilities, enhancing the learning experience, and building features
                like advanced speech analysis, personalized learning paths, and community
                collaboration tools. My goal is to make Lyder Talk the go-to platform for
                anyone looking to improve their speaking skills through structured practice.
              </p>
            </CardContent>
          </Card>

          {/* Connect With Me */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="text-center">Connect With Me</CardTitle>
              <CardDescription className="text-center">
                Let's connect and collaborate
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap justify-center gap-3">
                {socialLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Button
                      key={link.label}
                      variant="outline"
                      size="lg"
                      className="gap-2 hover:bg-primary hover:text-primary-foreground transition-colors group"
                      asChild
                    >
                      <a href={link.href} target="_blank" rel="noopener noreferrer">
                        <Icon className="w-5 h-5" />
                        {link.label}
                      </a>
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Helpful Links */}
          <Card className="mt-8">
            <CardContent className="pt-6">
              <div className="flex flex-wrap justify-center gap-3">
                <Button
                  variant="outline"
                  size="lg"
                  className="gap-2 hover:bg-primary hover:text-primary-foreground transition-colors"
                  onClick={() => navigate("/faq")}
                >
                  <HelpCircle className="w-5 h-5" />
                  FAQ
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default About;

